import React from "react";
import { PlanBadge, TemplateTypeBadge, ScanModeBadge } from "../badges";

const AVAILABLE_CATEGORIES = [
  "product_pages", "product_discoverability", "speed", "seo", "content",
  "pricing", "checkout", "mobile", "desktop", "translation", "amazon", "competitor",
];

const SettingsTab = ({ template, isEditing, editForm, setEditForm }) => {
  return (
    <div className="space-y-6">
      {/* Description */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">Description</h3>
        {isEditing ? (
          <textarea
            value={editForm.description || ""}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
            rows={3}
            placeholder="Template description..."
            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 focus:outline-none resize-y"
          />
        ) : (
          <p className="text-sm text-gray-600">
            {template.description || <span className="text-gray-400">No description</span>}
          </p>
        )}
      </div>

      {/* Launch Prompt */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">Launch Prompt</h3>
        <p className="text-xs text-gray-400 mb-2">
          The prompt the V2 orchestrator launches this agent with.
        </p>
        {isEditing ? (
          <textarea
            value={editForm.launch_prompt || ""}
            onChange={(e) => setEditForm({ ...editForm, launch_prompt: e.target.value })}
            rows={5}
            placeholder="Run the '...' agent on this site..."
            className="w-full px-3 py-2 border rounded-lg text-sm font-mono focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 focus:outline-none resize-y"
          />
        ) : (
          <p className="text-sm text-gray-600 whitespace-pre-wrap">
            {template.launch_prompt || <span className="text-gray-400">No launch prompt</span>}
          </p>
        )}
      </div>

      {/* Metadata grid */}
      <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
        <div>
          <span className="text-sm text-gray-500">Plan Type</span>
          <div className="mt-1">
            {isEditing ? (
              <select
                value={editForm.plan_type}
                onChange={(e) => setEditForm({ ...editForm, plan_type: e.target.value })}
                className="px-3 py-1 border rounded-lg text-sm"
              >
                <option value="core">CORE</option>
                <option value="premium">PREMIUM</option>
              </select>
            ) : (
              <PlanBadge plan={template.plan_type} />
            )}
          </div>
        </div>
        <div>
          <span className="text-sm text-gray-500">Scan Mode</span>
          <div className="mt-1">
            {isEditing ? (
              <select
                value={editForm.scan_mode || "scraper_one_look"}
                onChange={(e) => setEditForm({ ...editForm, scan_mode: e.target.value })}
                className="px-3 py-1 border rounded-lg text-sm"
              >
                <option value="scraper_one_look">Scraper</option>
                <option value="agentic_test">Agentic Test</option>
                <option value="agentic_test_definition">Discovery</option>
                <option value="broken_link">Broken Link</option>
                <option value="broken_image">Broken Image</option>
              </select>
            ) : (
              <ScanModeBadge mode={template.scan_mode} />
            )}
          </div>
        </div>
        <div>
          <span className="text-sm text-gray-500">Template Type</span>
          <div className="mt-1"><TemplateTypeBadge type={template.template_type} /></div>
        </div>
        <div>
          <span className="text-sm text-gray-500">Time (minutes)</span>
          {isEditing ? (
            <input
              type="number"
              value={editForm.time_minutes}
              onChange={(e) => setEditForm({ ...editForm, time_minutes: parseInt(e.target.value) || 0 })}
              className="w-24 px-2 py-1 border rounded-lg text-sm"
            />
          ) : (
            <p className="font-medium">{template.time_minutes}</p>
          )}
        </div>
        <div>
          <span className="text-sm text-gray-500">Automation Ready</span>
          {isEditing ? (
            <label className="flex items-center gap-2 mt-1">
              <input type="checkbox" checked={editForm.automation_ready} onChange={(e) => setEditForm({ ...editForm, automation_ready: e.target.checked })} className="rounded" />
              <span className="text-sm">Yes</span>
            </label>
          ) : (
            <p className="font-medium">{template.automation_ready ? "Yes" : "No"}</p>
          )}
        </div>
        <div>
          <span className="text-sm text-gray-500">Most Popular</span>
          {isEditing ? (
            <label className="flex items-center gap-2 mt-1">
              <input type="checkbox" checked={editForm.is_most_popular ?? false} onChange={(e) => setEditForm({ ...editForm, is_most_popular: e.target.checked })} className="rounded" />
              <span className="text-sm">Yes</span>
            </label>
          ) : (
            <p className="font-medium">{(template.is_most_popular ?? false) ? "Yes" : "No"}</p>
          )}
        </div>
        <div>
          <span className="text-sm text-gray-500">Show test cases on /launch</span>
          {isEditing ? (
            <label className="flex items-center gap-2 mt-1">
              <input type="checkbox" checked={editForm.show_test_cases ?? false} onChange={(e) => setEditForm({ ...editForm, show_test_cases: e.target.checked })} className="rounded" />
              <span className="text-sm">Yes</span>
            </label>
          ) : (
            <p className="font-medium">{(template.show_test_cases ?? false) ? "Yes" : "No"}</p>
          )}
        </div>
        <div>
          <span className="text-sm text-gray-500">Requires File</span>
          {isEditing ? (
            <label className="flex items-center gap-2 mt-1">
              <input type="checkbox" checked={editForm.requires_file ?? false} onChange={(e) => setEditForm({ ...editForm, requires_file: e.target.checked })} className="rounded" />
              <span className="text-sm">Yes</span>
            </label>
          ) : (
            <p className="font-medium">{(template.requires_file ?? false) ? "Yes" : "No"}</p>
          )}
        </div>
        <div>
          <span className="text-sm text-gray-500">Requires Scope</span>
          {isEditing ? (
            <label className="flex items-center gap-2 mt-1">
              <input type="checkbox" checked={editForm.requires_scope ?? false} onChange={(e) => setEditForm({ ...editForm, requires_scope: e.target.checked })} className="rounded" />
              <span className="text-sm">Yes</span>
            </label>
          ) : (
            <p className="font-medium">{(template.requires_scope ?? false) ? "Yes" : "No"}</p>
          )}
        </div>
        {(isEditing ? editForm.requires_file : template.requires_file) && (
          <div className="col-span-2">
            <span className="text-sm text-gray-500">Field Requirements</span>
            {isEditing ? (
              <div className="mt-1 space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-600">Required fields (one per line)</label>
                  <textarea
                    value={(editForm.field_requirements?.required || []).join("\n")}
                    onChange={(e) => {
                      const lines = e.target.value.split("\n").filter(l => l.trim());
                      setEditForm({ ...editForm, field_requirements: { ...(editForm.field_requirements || {}), required: lines } });
                    }}
                    rows={3}
                    placeholder={"Page URL\nExpected price"}
                    className="w-full px-2 py-1 border rounded-lg text-sm mt-1 font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Optional fields (one per line)</label>
                  <textarea
                    value={(editForm.field_requirements?.optional || []).join("\n")}
                    onChange={(e) => {
                      const lines = e.target.value.split("\n").filter(l => l.trim());
                      setEditForm({ ...editForm, field_requirements: { ...(editForm.field_requirements || {}), optional: lines } });
                    }}
                    rows={2}
                    placeholder={"Promotion %\nPromotion price"}
                    className="w-full px-2 py-1 border rounded-lg text-sm mt-1 font-mono"
                  />
                </div>
              </div>
            ) : (
              <div className="mt-1 text-sm">
                {template.field_requirements ? (
                  <div className="space-y-1">
                    {template.field_requirements.required?.length > 0 && (
                      <p><span className="font-medium text-gray-700">Required:</span> {template.field_requirements.required.join(", ")}</p>
                    )}
                    {template.field_requirements.optional?.length > 0 && (
                      <p><span className="font-medium text-gray-400">Optional:</span> {template.field_requirements.optional.join(", ")}</p>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-400">Not configured — will use defaults from agent type</span>
                )}
              </div>
            )}
          </div>
        )}
        <div>
          <span className="text-sm text-gray-500">Customers Can Run</span>
          {isEditing ? (
            <label className="flex items-center gap-2 mt-1">
              <input type="checkbox" checked={editForm.customers_can_run ?? false} onChange={(e) => setEditForm({ ...editForm, customers_can_run: e.target.checked })} className="rounded" />
              <span className="text-sm">Yes</span>
            </label>
          ) : (
            <p className="font-medium">{(template.customers_can_run ?? false) ? "Yes" : "No"}</p>
          )}
        </div>
      </div>

      {/* Browser Config */}
      {template.browser_config && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Browser Config</h3>
          <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <span className="text-sm text-gray-500">Viewport</span>
              <p className="font-medium">{template.browser_config.viewport_width || "—"} x {template.browser_config.viewport_height || "—"}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Locale</span>
              <p className="font-medium">{template.browser_config.locale || "—"}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Fixed IP</span>
              <p className="font-medium">{template.browser_config.fixed_ip ? "Yes" : "No"}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Use URL Locale</span>
              <p className="font-medium">{template.browser_config.use_url_locale ? "Yes" : "No"}</p>
            </div>
          </div>
        </div>
      )}

      {/* Categories */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">Categories</h3>
        {isEditing ? (
          <div className="flex flex-wrap gap-2">
            {AVAILABLE_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  const newCats = editForm.categories?.includes(cat) ? editForm.categories.filter((c) => c !== cat) : [...(editForm.categories || []), cat];
                  setEditForm({ ...editForm, categories: newCats });
                }}
                className={`px-2 py-1 rounded-full text-xs transition-colors ${editForm.categories?.includes(cat) ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
              >
                {cat}
              </button>
            ))}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {template.categories?.map((cat) => <span key={cat} className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700">{cat}</span>)}
            {(!template.categories || template.categories.length === 0) && <span className="text-sm text-gray-400">No categories</span>}
          </div>
        )}
      </div>

      {/* Tags */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">Tags</h3>
        {isEditing ? (
          <div>
            <div className="flex flex-wrap gap-2 mb-2">
              {editForm.tags?.map((tag, idx) => (
                <span key={idx} className="px-2 py-1 bg-blue-50 rounded-full text-xs text-blue-700 flex items-center gap-1">
                  {tag}
                  <button onClick={() => setEditForm({ ...editForm, tags: editForm.tags.filter((_, i) => i !== idx) })} className="text-blue-500 hover:text-blue-700">x</button>
                </span>
              ))}
            </div>
            <input type="text" placeholder="Add tag and press Enter" className="px-2 py-1 border rounded text-sm w-48"
              onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { setEditForm({ ...editForm, tags: [...(editForm.tags || []), e.target.value.trim()] }); e.target.value = ""; }}} />
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {template.tags?.map((tag) => <span key={tag} className="px-2 py-1 bg-blue-50 rounded-full text-xs text-blue-700">{tag}</span>)}
            {(!template.tags || template.tags.length === 0) && <span className="text-sm text-gray-400">No tags</span>}
          </div>
        )}
      </div>

      {/* Description Items */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">Description Items</h3>
        {isEditing ? (
          <div className="space-y-2">
            {editForm.description_items?.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="text-gray-400">-</span>
                <input type="text" value={item} onChange={(e) => { const newItems = [...editForm.description_items]; newItems[idx] = e.target.value; setEditForm({ ...editForm, description_items: newItems }); }} className="flex-1 px-2 py-1 border rounded text-sm" />
                <button onClick={() => setEditForm({ ...editForm, description_items: editForm.description_items.filter((_, i) => i !== idx) })} className="text-red-500 hover:text-red-700 text-sm">x</button>
              </div>
            ))}
            <button onClick={() => setEditForm({ ...editForm, description_items: [...(editForm.description_items || []), ""] })} className="text-sm text-indigo-600 hover:text-indigo-800">+ Add item</button>
          </div>
        ) : (
          <ul className="list-disc list-inside space-y-1">
            {template.description_items?.map((item, i) => <li key={i} className="text-sm text-gray-600">{item}</li>)}
            {(!template.description_items || template.description_items.length === 0) && <span className="text-sm text-gray-400">No description items</span>}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SettingsTab;
